import React from 'react';
import { Trans } from 'react-i18next';
import { Button, Icon, MenuItem } from 'hfs-common-ui';
import { MeasurementSystem } from '@fleet-bffs/api-contract';
import { InternationalizationNameSpace } from '~/constants/internationalization-namespace';
import MenuConstants from '~/constants/menu-constants';
import type { FleetMenuItem } from '~/components/containers/MenubarContainer/types/types';
import useAuthenticationState from '~/ducks/authentication/use-authentication-state';
import { useUpdateUserMeasurementSystemMutation } from '~/domain/settings/api/settings-api';
import * as S from './styled';

export interface Props {
  menuItems: MenuItem[];
  onSubmenuClick: (menuItem: MenuItem) => void;
}

const UserPreferencesSubmenu = ({ menuItems, onSubmenuClick }: Props) => {
  const [saveMeasurementSystem, { isLoading }] = useUpdateUserMeasurementSystemMutation();
  const { measurementSystem } = useAuthenticationState();
  const logoutItem = menuItems?.find(
    (item) => (item as FleetMenuItem)?.metadata?.key === MenuConstants.LOG_OUT
  ) as FleetMenuItem | undefined;
  const accountItem = menuItems?.find(
    (item) => (item as FleetMenuItem)?.metadata?.key === MenuConstants.ACCOUNT
  ) as FleetMenuItem | undefined;

  return (
    <S.WrapperDiv>
      {accountItem !== undefined && (
        <S.AccountLinkDiv>
          <Button
            icon={{
              position: 'left',
              type: 'action.settings_outlined',
            }}
            onClick={() => onSubmenuClick(accountItem)}
            size="small"
            variant="transparent"
          >
            <Trans
              i18nKey="account_settings.link"
              ns={InternationalizationNameSpace.UserSettings}
            />
            <Icon type="action.external_link" size="medium" />
          </Button>
        </S.AccountLinkDiv>
      )}
      {measurementSystem !== undefined && (
        <S.PreferencesDiv>
          <S.PreferencesLabelDiv>
            <Trans i18nKey="preferences.title" ns={InternationalizationNameSpace.UserSettings} />
          </S.PreferencesLabelDiv>
          <S.MeasurementUnitsWrapperDiv>
            <S.MeasurementSystemLabelDiv>
              <Trans
                i18nKey="measurement_system.type.label"
                ns={InternationalizationNameSpace.UserSettings}
              />
            </S.MeasurementSystemLabelDiv>
            <S.MeasurementUnitsButtonsDiv>
              <Button
                size="medium"
                variant={measurementSystem === MeasurementSystem.Metric ? 'primary' : 'transparent'}
                onClick={() =>
                  saveMeasurementSystem({ measurementSystem: MeasurementSystem.Metric })
                }
                disabled={isLoading}
              >
                <Trans
                  i18nKey="measurement_system.type.metric"
                  ns={InternationalizationNameSpace.UserSettings}
                />
              </Button>
              <Button
                size="medium"
                variant={
                  measurementSystem === MeasurementSystem.Imperial ? 'primary' : 'transparent'
                }
                onClick={() =>
                  saveMeasurementSystem({ measurementSystem: MeasurementSystem.Imperial })
                }
                disabled={isLoading}
              >
                <Trans
                  i18nKey="measurement_system.type.imperial"
                  ns={InternationalizationNameSpace.UserSettings}
                />
              </Button>
            </S.MeasurementUnitsButtonsDiv>
          </S.MeasurementUnitsWrapperDiv>
        </S.PreferencesDiv>
      )}
      {logoutItem !== undefined && (
        <S.LogoutLinkDiv>
          <Button
            icon={{
              position: 'left',
              type: 'action.log_out',
            }}
            onClick={() => onSubmenuClick(logoutItem)}
            size="small"
            variant="transparent"
          >
            {logoutItem.label}
          </Button>
        </S.LogoutLinkDiv>
      )}
    </S.WrapperDiv>
  );
};

export default UserPreferencesSubmenu;
